import { useEffect, useState } from "react";

/** The one query that answers it, in one place. */
const STILL_QUERY = "(prefers-reduced-motion: reduce)";

/** Whether the reader has asked for less movement, read once. */
const wantsStillness = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(STILL_QUERY).matches;

/**
 * The same answer, kept current.
 *
 * Subscribed rather than read once, because it governs two things wherever it
 * is used: whether a strip advances on its own, and whether it slides when it
 * does. Content that moves by itself is what that setting is most plainly
 * about, so for anybody who has turned it on a carousel neither advances nor
 * animates — pressing a dot simply puts the next card there. Every slide stays
 * reachable.
 *
 * Shared rather than written out at each carousel: the two in the app — the
 * week's appointments on a client, and the brand's pitch on a phone — have to
 * come to the same answer about the same setting, and a copy of this in each
 * is two places for that answer to drift.
 *
 * Environments without `matchMedia` — jsdom under the test runner — count as
 * no preference, which is the same default `useIsMobile` takes.
 */
export const useStillness = (): boolean => {
  const [still, setStill] = useState(wantsStillness);

  useEffect(() => {
    const query = window.matchMedia?.(STILL_QUERY);
    if (!query) return;

    const read = () => setStill(query.matches);
    read();
    query.addEventListener?.("change", read);
    return () => query.removeEventListener?.("change", read);
  }, []);

  return still;
};

export default useStillness;
